import { motion } from "framer-motion";
import { MessageCircle, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface AIConciergeButtonProps {
  isOpen: boolean;
  onClick: () => void;
  hasUnread?: boolean;
}

export const AIConciergeButton = ({ isOpen, onClick, hasUnread }: AIConciergeButtonProps) => {
  return (
    <motion.button
      onClick={onClick}
      className={cn(
        "fixed bottom-24 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full md:bottom-6",
        "bg-[radial-gradient(circle_at_30%_25%,#4480BA,#2A629E_45%,#0C1B32)] text-white",
        "border border-[#9CCBFF]/40 hover:border-[#9CCBFF]/80",
        "shadow-[0_10px_30px_-6px_rgba(42,98,158,0.7)] hover:shadow-[0_16px_44px_-6px_rgba(74,138,201,0.85)]",
        "transition-all duration-300",
        "focus:outline-none focus:ring-2 focus:ring-[#4A8AC9]/60 focus:ring-offset-2 focus:ring-offset-[#07101F]"
      )}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 260, damping: 20 }}
      aria-label={isOpen ? "Close chat" : "Open AI Concierge"}
    >
      <motion.div
        initial={false}
        animate={{ rotate: isOpen ? 90 : 0 }}
        transition={{ duration: 0.2 }}
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <MessageCircle className="h-6 w-6" />
        )}
      </motion.div>
      
      {/* Unread indicator */}
      {!isOpen && hasUnread && (
        <motion.span
          className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
        >
          1
        </motion.span>
      )}
      
      {/* Pulse animation when closed */}
      {!isOpen && (
        <motion.span
          className="absolute inset-0 -z-10 rounded-full border border-[#4A8AC9]/60"
          animate={{
            scale: [1, 1.45],
            opacity: [0.7, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeOut",
          }}
        />
      )}
    </motion.button>
  );
};
